import { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, Building2, Calendar, CreditCard, MapPin, Mail, Globe, FileText, Info, Briefcase, Shield, Building, Hash, Phone } from 'lucide-react';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { SicCodeSelector } from '@/components/ui/sic-code-selector';
import { SicCode } from '@/data/sicCodes';
import { useIndustryClassifications } from '../hooks/useIndustryClassifications';

interface CorporationInfoStepProps {
  onSubmit: (data: { corporationInfo: any }) => void;
  onPrevious?: () => void;
  initialData?: any;
  shouldShowField?: (fieldPath: string) => boolean;
  isFieldRequired?: (fieldPath: string) => boolean;
  getFieldDefaultValue?: (fieldPath: string, includeWhenVisible?: boolean) => string | undefined;
  sectionTitle?: string;
  sectionSubtitle?: string;
  applicationSource?: string;
  applicationType?: 'individual' | 'corporation';
}

const CORPORATION_TYPES = [
  { value: "llc", label: "Limited Liability Company (LLC)" },
  { value: "s_corp", label: "S Corporation" },
  { value: "c_corp", label: "C Corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "lp", label: "Limited Partnership (LP)" },
  { value: "llp", label: "Limited Liability Partnership (LLP)" },
  { value: "non_profit", label: "Non-Profit Organization" },
];

export function CorporationInfoStep({
  onSubmit,
  onPrevious,
  initialData,
  shouldShowField = () => true,
  isFieldRequired = () => true,
  getFieldDefaultValue,
  sectionTitle = "Corporation Information",
  sectionSubtitle = "Please provide details about your business entity. This information will be used for underwriting and compliance purposes.",
  applicationSource,
  applicationType = 'corporation',
}: CorporationInfoStepProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load industry classifications based on application source
  const { allEnabledCodes, loading: industryCodesLoading } = useIndustryClassifications(applicationSource);

  // Create a wrapper function that hides certain fields for individuals
  const shouldShowFieldForType = (fieldPath: string): boolean => {
    // For individuals, hide these fields
    if (applicationType === 'individual') {
      const hiddenFieldsForIndividuals = [
        'corporationInfo.corporationType',
        'corporationInfo.ein',
      ];

      if (hiddenFieldsForIndividuals.includes(fieldPath)) {
        return false;
      }
    }

    return shouldShowField(fieldPath);
  };

  // Create dynamic schema based on conditional requirements
  const corporationInfoSchema = useMemo(() => {
    const baseSchema: any = {};

    if (shouldShowFieldForType('corporationInfo.businessName')) {
      baseSchema.businessName = isFieldRequired('corporationInfo.businessName')
        ? z.string().min(1, 'Business name is required').max(100, 'Business name is too long')
        : z.string().max(100, 'Business name is too long').optional();
    }

    if (shouldShowFieldForType('corporationInfo.corporationType')) {
      baseSchema.corporationType = isFieldRequired('corporationInfo.corporationType')
        ? z.string().min(1, 'Corporation type is required')
        : z.string().optional();
    }

    if (shouldShowFieldForType('corporationInfo.businessStartDate')) {
      const dateValidation = z.string().refine((date) => {
        if (!date) return true;
        const startDate = new Date(date);
        const today = new Date();
        return startDate <= today;
      }, 'Business start date cannot be in the future');

      baseSchema.businessStartDate = isFieldRequired('corporationInfo.businessStartDate')
        ? dateValidation.refine((val) => val.length > 0, 'Business start date is required')
        : dateValidation.optional();
    }

    if (shouldShowFieldForType('corporationInfo.ein')) {
      baseSchema.ein = isFieldRequired('corporationInfo.ein')
        ? z.string().min(10, 'Please enter a valid EIN').max(10, 'Please enter a valid EIN').regex(/^\d{2}-\d{7}$/, 'EIN must be in format XX-XXXXXXX')
        : z.string().regex(/^\d{2}-\d{7}$/, 'EIN must be in format XX-XXXXXXX').optional();
    }

    if (shouldShowFieldForType('corporationInfo.dba')) {
      baseSchema.dba = z.string().optional();
    }

    if (shouldShowFieldForType('corporationInfo.website')) {
      baseSchema.website = isFieldRequired('corporationInfo.website')
        ? z.string().min(1, 'Website is required').url('Please enter a valid website URL')
        : z.string().url('Please enter a valid website URL').optional();
    }

    if (shouldShowFieldForType('corporationInfo.businessDescription')) {
      baseSchema.businessDescription = isFieldRequired('corporationInfo.businessDescription')
        ? z.string().min(1, 'Business description is required')
        : z.string().optional();
    }

    if (shouldShowFieldForType('corporationInfo.sicCode')) {
      baseSchema.sicCode = isFieldRequired('corporationInfo.sicCode')
        ? z.string().min(1, 'SIC/MCC code is required')
        : z.string().optional();
    }

    if (shouldShowFieldForType('corporationInfo.sicDescription')) {
      baseSchema.sicDescription = z.string().optional();
    }

    if (shouldShowFieldForType('corporationInfo.customerServiceEmail')) {
      baseSchema.customerServiceEmail = isFieldRequired('corporationInfo.customerServiceEmail')
        ? z.string().email('Invalid email address')
        : z.string().email('Invalid email address').optional();
    }

    if (shouldShowFieldForType('corporationInfo.customerServicePhone')) {
      baseSchema.customerServicePhone = isFieldRequired('corporationInfo.customerServicePhone')
        ? z.string().min(7, 'Enter a valid phone number')
        : z.string().optional();
    }


    // sameAsPersonalAddress removed - no personal address to compare to

    if (shouldShowFieldForType('corporationInfo.businessAddress')) {
      const addressSchema = {
        street: isFieldRequired('corporationInfo.businessAddress.street')
          ? z.string().min(1, 'Street address is required')
          : z.string().optional(),
        city: isFieldRequired('corporationInfo.businessAddress.city')
          ? z.string().min(1, 'City is required')
          : z.string().optional(),
        state: isFieldRequired('corporationInfo.businessAddress.state')
          ? z.string().min(2, 'State is required')
          : z.string().optional(),
        zipCode: isFieldRequired('corporationInfo.businessAddress.zipCode')
          ? z.string().min(5, 'ZIP code is required')
          : z.string().optional(),
        fullAddress: isFieldRequired('corporationInfo.businessAddress.fullAddress')
          ? z.string().min(1, 'Full address is required')
          : z.string().optional(),
      };
      baseSchema.businessAddress = z.object(addressSchema);
    }

    return z.object(baseSchema);
  }, [shouldShowField, isFieldRequired, applicationType]);

  type CorporationInfoData = z.infer<typeof corporationInfoSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CorporationInfoData>({
    resolver: zodResolver(corporationInfoSchema),
    defaultValues: {
      businessName: initialData?.corporationInfo?.businessName || getFieldDefaultValue?.('corporationInfo.businessName') || '',
      dba: initialData?.corporationInfo?.dba || getFieldDefaultValue?.('corporationInfo.dba') || '',
      corporationType: initialData?.corporationInfo?.corporationType || getFieldDefaultValue?.('corporationInfo.corporationType') || (applicationType === 'individual' ? 'sole_proprietorship' : ''),
      businessStartDate: initialData?.corporationInfo?.businessStartDate || getFieldDefaultValue?.('corporationInfo.businessStartDate') || '',
      ein: initialData?.corporationInfo?.ein || getFieldDefaultValue?.('corporationInfo.ein') || '',
      website: initialData?.corporationInfo?.website || getFieldDefaultValue?.('corporationInfo.website') || '',
      businessDescription: initialData?.corporationInfo?.businessDescription || getFieldDefaultValue?.('corporationInfo.businessDescription') || '',
      sicCode: initialData?.corporationInfo?.sicCode || getFieldDefaultValue?.('corporationInfo.sicCode') || '',
      sicDescription: initialData?.corporationInfo?.sicDescription || getFieldDefaultValue?.('corporationInfo.sicDescription') || '',
      customerServiceEmail: initialData?.corporationInfo?.customerServiceEmail || getFieldDefaultValue?.('corporationInfo.customerServiceEmail') || '',
      customerServicePhone: initialData?.corporationInfo?.customerServicePhone || getFieldDefaultValue?.('corporationInfo.customerServicePhone') || '',
      // sameAsPersonalAddress removed
      businessAddress: initialData?.corporationInfo?.businessAddress || {
        street: getFieldDefaultValue?.('corporationInfo.businessAddress.street') || '',
        city: getFieldDefaultValue?.('corporationInfo.businessAddress.city') || '',
        state: getFieldDefaultValue?.('corporationInfo.businessAddress.state') || '',
        zipCode: getFieldDefaultValue?.('corporationInfo.businessAddress.zipCode') || '',
        fullAddress: getFieldDefaultValue?.('corporationInfo.businessAddress.fullAddress') || '',
      },
    },
    mode: 'onBlur',
  });

  // sameAsPersonalAddress watch removed

  const handleEINChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    let formattedValue = value;
    
    if (value.length >= 2) {
      formattedValue = value.slice(0, 2) + '-' + value.slice(2, 9);
    }
    
    setValue('ein', formattedValue, {
      shouldValidate: false,
    });
  };


  // handleSameAddressChange removed - no personal address to use

  const handleBusinessAddressChange = (address: any) => {
    setValue('businessAddress', address, {
      shouldValidate: true,
    });
  };

  const onFormSubmit = async (data: CorporationInfoData) => {
    try {
      setIsSubmitting(true);
      setServerError(null);
      // Merge in default values for hidden fields so they persist even if not shown
      const merged: any = { ...data };

      const simpleFields = [
        'businessName',
        'corporationType',
        'businessStartDate',
        'ein',
        'dba',
        'website',
        'businessDescription',
        'sicCode',
        'sicDescription',
        'customerServiceEmail',
        'customerServicePhone',
      ] as const;

      simpleFields.forEach((field) => {
        const fieldPath = `corporationInfo.${field}`;
        if (!shouldShowFieldForType(fieldPath)) {
          // For individuals, set specific defaults
          if (applicationType === 'individual') {
            if (field === 'corporationType') {
              merged[field] = 'sole_proprietorship';
            }
            // EIN will be handled by API using SSN from beneficial owner
          } else {
            const defVal = getFieldDefaultValue?.(fieldPath);
            if (defVal !== undefined) merged[field] = defVal;
          }
        }
      });

      // Handle businessAddress nested fields
      const addressFields = ['street', 'city', 'state', 'zipCode', 'fullAddress'] as const;
      const existingAddress = merged.businessAddress || {};
      const address: any = { ...existingAddress };
      addressFields.forEach((sub) => {
        const fieldPath = `corporationInfo.businessAddress.${sub}`;
        if (!shouldShowFieldForType(fieldPath)) {
          const defVal = getFieldDefaultValue?.(fieldPath);
          if (defVal !== undefined) address[sub] = defVal;
        }
      });
      merged.businessAddress = address;

      await onSubmit({ corporationInfo: merged });
    } catch (error) {
      console.error('Submission error:', error);
      setServerError('An error occurred while saving your information');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-gray-50/50">
          <CardHeader className="pb-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Building2 className="h-6 w-6" />
              </div>
              {sectionTitle}
            </CardTitle>
            <CardDescription className="text-blue-50 text-base mt-2">
              {sectionSubtitle}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-8 p-8">
            {serverError && (
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-red-800">{serverError}</AlertDescription>
              </Alert>
            )}

            {/* Business Identity Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Building className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Business Identity</h3>
              </div>
              
              <div className="space-y-6">
                {/* Business Legal Name and DBA */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {shouldShowFieldForType('corporationInfo.businessName') && (
                    <div className="space-y-3">
                      <Label htmlFor="businessName" className="text-sm font-medium text-gray-700">
                        Business Legal Name {isFieldRequired('corporationInfo.businessName') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative group">
                        <Input
                          id="businessName"
                          {...register('businessName')}
                          placeholder="ABC Company LLC"
                          className="pl-11 h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200 group-hover:border-gray-300"
                        />
                        <Building2 className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors duration-200" />
                      </div>
                      {errors.businessName && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {String(errors.businessName.message)}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Enter the exact legal name as it appears on your business registration documents.
                      </p>
                    </div>
                  )}

                  {shouldShowFieldForType('corporationInfo.dba') && (
                    <div className="space-y-3">
                      <Label htmlFor="dba" className="text-sm font-medium text-gray-700">
                        DBA (Doing Business As) {isFieldRequired('corporationInfo.dba') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative group">
                        <Input
                          id="dba"
                          {...register('dba')}
                          placeholder="Trade name (optional)"
                          className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200 group-hover:border-gray-300"
                        />
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">
                        This will be the name that will be used on the statement descriptor.
                      </p>
                    </div>
                  )}
                </div>

                {/* Business Address */}
                {shouldShowFieldForType('corporationInfo.businessAddress') && (
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-700">
                      Business Address {isFieldRequired('corporationInfo.businessAddress') && <span className="text-red-500">*</span>}
                    </Label>
                    <Controller
                      name="businessAddress"
                      control={control}
                      render={({ field }) => (
                        <AddressAutocomplete
                          label=""
                          placeholder="Enter business address..."
                          required={isFieldRequired('corporationInfo.businessAddress')}
                          value={field.value}
                          onChange={handleBusinessAddressChange}
                          error={(() => {
                            if (!errors.businessAddress) return undefined;
                            if (typeof errors.businessAddress.message === 'string') return errors.businessAddress.message;
                            const addrErrors = errors.businessAddress as any;
                            return addrErrors.street?.message ||
                                   addrErrors.city?.message ||
                                   addrErrors.state?.message ||
                                   addrErrors.zipCode?.message ||
                                   addrErrors.fullAddress?.message;
                          })()}
                        />
                      )}
                    />
                    <p className="text-xs text-gray-500 leading-relaxed">
                      This will be used as your primary business location for all official correspondence.
                    </p>
                  </div>
                )}

                {/* Corporation Type */}
                {shouldShowFieldForType('corporationInfo.corporationType') && (
                  <div className="space-y-3">
                    <Label htmlFor="corporationType" className="text-sm font-medium text-gray-700">
                      Corporation Type {isFieldRequired('corporationInfo.corporationType') && <span className="text-red-500">*</span>}
                    </Label>
                    <Controller
                      name="corporationType"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20">
                            <SelectValue placeholder="Select your business entity type" />
                          </SelectTrigger>
                          <SelectContent>
                            {CORPORATION_TYPES.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.corporationType && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {String(errors.corporationType.message)}
                      </p>
                    )}
                  </div>
                )}

                {/* Business Start Date and EIN */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {shouldShowFieldForType('corporationInfo.businessStartDate') && (
                    <div className="space-y-3">
                      <Label htmlFor="businessStartDate" className="text-sm font-medium text-gray-700">
                        Business Start Date {isFieldRequired('corporationInfo.businessStartDate') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative group">
                        <Input
                          id="businessStartDate"
                          type="date"
                          {...register('businessStartDate')}
                          className="pl-11 h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200 group-hover:border-gray-300"
                          max={new Date().toISOString().split('T')[0]}
                        />
                        <Calendar className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors duration-200" />
                      </div>
                      {errors.businessStartDate && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {String(errors.businessStartDate.message)}
                        </p>
                      )}
                    </div>
                  )}

                  {shouldShowFieldForType('corporationInfo.ein') && (
                    <div className="space-y-3">
                      <Label htmlFor="ein" className="text-sm font-medium text-gray-700">
                        EIN (Tax ID) {isFieldRequired('corporationInfo.ein') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative group">
                        <Input
                          id="ein"
                          onChange={handleEINChange}
                          value={watch('ein')}
                          placeholder="XX-XXXXXXX"
                          maxLength={10}
                          className="pl-11 h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200 group-hover:border-gray-300"
                        />
                        <Hash className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors duration-200" />
                      </div>
                      {errors.ein && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {String(errors.ein.message)}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Your Employer Identification Number from the IRS.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Business Profile Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="h-5 w-5 text-emerald-600" />
                <h3 className="text-lg font-semibold text-gray-900">Business Profile</h3>
              </div>
              
              <div className="space-y-6">
                {/* Business Description */}
                {shouldShowFieldForType('corporationInfo.businessDescription') && (
                  <div className="space-y-3">
                    <Label htmlFor="businessDescription" className="text-sm font-medium text-gray-700">
                      Business Description {isFieldRequired('corporationInfo.businessDescription') && <span className="text-red-500">*</span>}
                    </Label>
                    <Textarea
                      id="businessDescription"
                      {...register('businessDescription')}
                      placeholder="Describe what your business does, the products or services you offer, and your target market..."
                      rows={4}
                      className="border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 resize-none transition-all duration-200"
                    />
                    {errors.businessDescription && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {String(errors.businessDescription.message)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Provide a clear description of your business activities and services.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {shouldShowFieldForType('corporationInfo.website') && (
                    <div className="space-y-3">
                      <Label htmlFor="website" className="text-sm font-medium text-gray-700">
                        Website {isFieldRequired('corporationInfo.website') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative group">
                        <Input
                          id="website"
                          {...register('website')}
                          placeholder="https://example.com"
                          className="pl-11 h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200 group-hover:border-gray-300"
                        />
                        <Globe className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400 group-hover:text-emerald-500 transition-colors duration-200" />
                      </div>
                      {errors.website && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {String(errors.website.message)}
                        </p>
                      )}
                    </div>
                  )}

                  {shouldShowFieldForType('corporationInfo.sicCode') && (
                    <div className="space-y-3">
                      <SicCodeSelector
                        value={watch('sicCode')}
                        valueDescription={watch('sicDescription')}
                        onValueChange={(sicCode: SicCode | null) => {
                          setValue('sicCode', sicCode?.code || '');
                          setValue('sicDescription', sicCode?.description || '');
                        }}
                        placeholder="Search and select your business category"
                        required={isFieldRequired('corporationInfo.sicCode')}
                        error={errors.sicCode?.message as string}
                        dynamicCodes={allEnabledCodes}
                        loading={industryCodesLoading}
                      />
                      <p className="text-xs text-gray-500 leading-relaxed">
                        Select the category that best describes your primary business activity.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Customer Service Contact - Only show section if at least one field is visible */}
            {(shouldShowFieldForType('corporationInfo.customerServiceEmail') || shouldShowFieldForType('corporationInfo.customerServicePhone')) && (
              <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="h-5 w-5 text-cyan-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Customer Support</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {shouldShowFieldForType('corporationInfo.customerServiceEmail') && (
                    <div className="space-y-3">
                      <Label htmlFor="customerServiceEmail" className="text-sm font-medium text-gray-700">
                        Customer Service Email {isFieldRequired('corporationInfo.customerServiceEmail') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative group">
                        <Input
                          id="customerServiceEmail"
                          {...register('customerServiceEmail')}
                          type="email"
                          placeholder="support@example.com"
                          className="pl-11 h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200 group-hover:border-gray-300"
                        />
                        <Mail className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400 group-hover:text-cyan-500 transition-colors duration-200" />
                      </div>
                      {errors.customerServiceEmail && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {String(errors.customerServiceEmail.message)}
                        </p>
                      )}
                    </div>
                  )}

                  {shouldShowFieldForType('corporationInfo.customerServicePhone') && (
                    <div className="space-y-3">
                      <Label htmlFor="customerServicePhone" className="text-sm font-medium text-gray-700">
                        Customer Service Phone {isFieldRequired('corporationInfo.customerServicePhone') && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="relative group">
                        <Input
                          id="customerServicePhone"
                          {...register('customerServicePhone')}
                          type="tel"
                          placeholder="(555) 123-4567"
                          className="pl-11 h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200 group-hover:border-gray-300"
                        />
                        <Phone className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors duration-200" />
                      </div>
                      {errors.customerServicePhone && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {String(errors.customerServicePhone.message)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Business Address section moved up after Legal Name and DBA */}

            {/* Form Actions */}
            <div className="flex justify-between pt-8 border-t border-gray-100">
              {onPrevious && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onPrevious}
                  className="px-6 py-2.5 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                >
                  Previous
                </Button>
              )}
              <Button 
                type="submit" 
                disabled={isSubmitting} 
                className="ml-auto px-8 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Saving...
                  </span>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
